import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import type { AdminCreditAdjustmentInput } from "@/features/admin/admin.types";
import { validateCreditAdjustmentInput } from "@/features/admin/adminValidation";

type DeductCreditsDialogProps = {
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: Omit<AdminCreditAdjustmentInput, "userId">) => void;
  open: boolean;
};

export function DeductCreditsDialog({ busy = false, onOpenChange, onSubmit, open }: DeductCreditsDialogProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("50");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Partial<Record<"amount" | "reason", string>>>({});

  useEffect(() => {
    if (open) {
      setAmount("50");
      setReason("");
      setErrors({});
    }
  }, [open]);

  function handleSubmit() {
    const input = {
      amount: Number.parseInt(amount, 10),
      reason,
    };
    const validationErrors = validateCreditAdjustmentInput(input);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    onSubmit({
      amount: input.amount,
      reason: reason.trim(),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminWorkspace.dialogs.deductCredits.title")}</DialogTitle>
          <DialogDescription>{t("adminWorkspace.dialogs.deductCredits.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Input placeholder={t("adminWorkspace.dialogs.shared.amountPlaceholder")} type="number" value={amount} onChange={(event) => setAmount(event.target.value)} />
            {errors.amount ? <FormMessage>{t(errors.amount)}</FormMessage> : null}
          </div>
          <textarea
            className="min-h-28 w-full rounded-[1.75rem] border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
            placeholder={t("adminWorkspace.dialogs.shared.reasonPlaceholder")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {errors.reason ? <FormMessage>{t(errors.reason)}</FormMessage> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={busy}
            type="button"
            variant="destructive"
            onClick={handleSubmit}
          >
            {busy ? t("common.saving") : t("adminWorkspace.dialogs.deductCredits.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
